import { toast } from "react-toastify";
import { isEnabled } from "utils/featureFlags";

import moment from "moment";

import * as BlueprintAPI from "api/BlueprintAPI";
import * as DatasetAPI from "api/DatasetAPI";

import * as homeReducer from "containers/Home/reducer";
import * as viewReducer from "containers/View/reducer";
import * as foldersReducer from "containers/Folders/reducer";
import * as blueprintReducer from "containers/Blueprint/reducer";
import * as transformReducer from "containers/Dataset/reducer";
import * as setupReducer from "containers/Setup/reducer";

export const GALLERY_RIGHT_MODE = {
  LINEAGE: "lineage",
  EXPLORE: "explore",
  AIREADY: "ai ready",
  EDIT: "edit",
  FIELD: "field",
  OUTCOME: "outcome",
  COMBINE: "combine",
  APPEND: "append",
  FILTER: "filter",
  DERIVED: "derived",
  BLUEPRINT: "blueprint",
  METADATA: "metadata",
  FEEDBACK: "feedback"
};

export const JOIN_TYPES = {
  LOOKUP: "lookup",
  LEFT_JOIN: "left join",
  RIGHT_JOIN: "right join",
  INNER_JOIN: "inner join",
  FULL_OUTER_JOIN: "full outer join"
};

const UPDATE_FIELD = "gallery/UPDATE_FIELD";
const UPDATE_FIELD_VALUE = "gallery/UPDATE_FIELD_VALUE";

const initialState = {};

function galleryReducer(state = initialState, action) {
  switch (action.type) {
    case UPDATE_FIELD:
      return {
        ...state,
        [action.id]: action.data
      };
    case UPDATE_FIELD_VALUE:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          [action.key]: action.data
        }
      };
    default:
      return state;
  }
}

export function graphSort(data, label) {
  const isNumber = (num) => {
    return num === "inf" || num === "-inf" || !isNaN(num);
  };

  const compareNumbers = (num1, num2) => {
    if (num1 === "inf" || num2 === "-inf") {
      return 1;
    }
    if (num1 === "-inf" || num2 === "inf") {
      return -1;
    }
    return Number(num1) - Number(num2);
  };

  let sortType = null;
  for (let i = 0; i < data.length; i++) {
    const d = data[i];
    const cLabel = d[label];
    if (!cLabel) {
      return data;
    }
    const sLabel = cLabel.split(" to ");
    if (sLabel.length == 2 && isNumber(sLabel[0])) {
      if (sortType === null || sortType === "split") {
        sortType = "split";
      } else {
        sortType = "alphabet";
        break;
      }
    } else if (isNumber(cLabel)) {
      if (sortType === null || sortType === "number") {
        sortType = "number";
      } else {
        sortType = "alphabet";
        break;
      }
    } else {
      sortType = "alphabet";
      break;
    }
  }

  let retData = [...data];
  retData.sort((a, b) => {
    let aLabel = a[label];
    let bLabel = b[label];
    if (sortType === "split") {
      let aNum = aLabel.split(" to ")[0];
      let bNum = bLabel.split(" to ")[0];
      return compareNumbers(aNum, bNum);
    } else if (sortType === "number") {
      return compareNumbers(aLabel, bLabel);
    } else if (sortType === "alphabet") {
      return aLabel < bLabel ? -1 : 1;
    }
  });
  return retData;
}

/* Selectors */
export function getIsEDG() {
  return isEnabled("edg");
}

export function getBase(state) {
  return state.gallery;
}

export function getGallery(state, id) {
  return getBase(state) ? getBase(state)[id] : undefined;
}

export function getRightMode(state, id) {
  return getGallery(state, id) ? getGallery(state, id).rightMode : undefined;
}

export function getViewField(state, id) {
  return getGallery(state, id) ? getGallery(state, id).viewField : undefined;
}

export function getViewOutcome(state, id) {
  return getGallery(state, id) ? getGallery(state, id).viewOutcome : undefined;
}

export function getCombineDataset(state, id) {
  return getGallery(state, id) ? getGallery(state, id).combineDataset : undefined;
}

export function getLookupKeys(state, id) {
  return getGallery(state, id) ? getGallery(state, id).lookupKeys : {};
}

export function getFilters(state, id) {
  return getGallery(state, id) ? getGallery(state, id).filters : {};
}

export function getMatchedColumns(state, id) {
  return getGallery(state, id) ? getGallery(state, id).matchedColumns : {};
}

export function getIgnoreOutcomes(state, id) {
  return getGallery(state, id) ? getGallery(state, id).ignoreOutcomes : {};
}

export function getExploreOutcomes(state, id) {
  const analyzedOutcomes = homeReducer.getAnalyzedOutcomes(state, id);
  const ignoredOutcomes = getIgnoreOutcomes(state, id) || {};
  const retOutcomes = {};
  analyzedOutcomes &&
    analyzedOutcomes.forEach((outcome) => {
      if (ignoredOutcomes[outcome] !== true) {
        retOutcomes[outcome] = true;
      }
    });

  const target = homeReducer.getTarget(state, id);

  if (target) {
    retOutcomes[target] = true;
  }

  return retOutcomes;
}

export function getJoinType(state, id) {
  return getGallery(state, id) && getGallery(state, id).joinType ? getGallery(state, id).joinType : JOIN_TYPES.LOOKUP;
}

export function getAttachedBlueprints(state, id) {
  return getGallery(state, id) && getGallery(state, id).attachedBlueprints
    ? getGallery(state, id).attachedBlueprints
    : {};
}

export function getAttachedBlueprintId(state, id) {
  return getGallery(state, id) ? getGallery(state, id).attachedBlueprintId : undefined;
}

export function getColumnBlueprintId(state, id) {
  return getGallery(state, id) ? getGallery(state, id).columnBlueprintId : undefined;
}

export function getIsSaving(state, id) {
  return getGallery(state, id) ? getGallery(state, id).isSaving : undefined;
}

export function getSortField(state, id) {
  return getGallery(state, id) ? getGallery(state, id).sortField : undefined;
}

export function getStopReset(state, id) {
  return getGallery(state, id) ? getGallery(state, id).stopReset : undefined;
}

export function getIsDatasetUpdating(state, id) {
  return getGallery(state, id) ? getGallery(state, id).isDatasetUpdating : undefined;
}

export function getAddChartId(state, id) {
  return getGallery(state, id) ? getGallery(state, id).addChartId : undefined;
}

export function getHighlightField(state, id) {
  return getGallery(state, id) ? getGallery(state, id).highlightField : undefined;
}

export function getIsPin(state, id) {
  return getGallery(state, id) ? getGallery(state, id).isPin : {};
}

export function getIsBlueprintSaved(state, id) {
  return getGallery(state, id) ? getGallery(state, id).isBlueprintSaved : undefined;
}

export function getIsBlueprintSaving(state, id) {
  return getGallery(state, id) ? getGallery(state, id).isBlueprintSaving : undefined;
}

export function getIsCreatingLinkedReport(state, id) {
  return getGallery(state, id) ? getGallery(state, id).isCreatingLinkedReport : undefined;
}

export function getOutcomeSuccess(state, id) {
  const target = homeReducer.getTarget(state, id);
  const targetValue = homeReducer.getTargetValue(state, id);

  if (homeReducer.getOutcomeType(state, id) === "number") {
    return undefined;
  }

  return (
    getGallery(state, id)?.outcomeSuccess ??
    (target !== undefined && targetValue !== undefined
      ? {
          [target]: {
            label: targetValue,
            value: targetValue
          }
        }
      : undefined)
  );
}

export function hasUpdatedOutcomeSuccess(state, id) {
  const outcomeSuccess = getOutcomeSuccess(state, id);
  const currentTarget = homeReducer.getTarget(state, id);
  const currentValue = homeReducer.getTargetValue(state, id);

  return outcomeSuccess?.[currentTarget]?.value !== currentValue;
}

export function getIsExploring(state, id) {
  const dataset = homeReducer.getDataset(state, id);
  if (!dataset) {
    return undefined;
  }
  const outcome = dataset.target;
  const noOutcome = dataset.status && dataset.status.indexOf("missing_target_variable") !== -1;

  const finishedIndexing =
    dataset.status &&
    dataset.status.indexOf("indexing") === -1 &&
    dataset.status.indexOf("finishing_indexing") === -1 &&
    !dataset.stack_id;

  const isError = dataset.errors && dataset.errors.length > 0;

  const isDone = dataset.rowCount !== -1 || finishedIndexing;
  const isLoading = !isDone && !isError;
  return isLoading ? outcome : undefined;
}

export function getBlueprints(state, id) {
  const blueprints = viewReducer.getBlueprints(state);

  return blueprints.filter((b) => b.wizard && b.wizard.gallery === undefined);
}

export function getDatasets(state, id) {
  const datasets = viewReducer.getDatasets(state);
  const blueprints = viewReducer.getBlueprints(state);

  return datasets;
}

export function getAppendDataset(state, id) {
  return getGallery(state, id) ? getGallery(state, id).appendDataset : undefined;
}

/* Actions */
export function updateGallery(id, gallery) {
  return { type: UPDATE_FIELD, id: id, data: gallery };
}

export function updateCombineDataset(id, combineDataset) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: combineDataset,
    key: "combineDataset"
  };
}

export function updateAppendDataset(id, appendDataset) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: appendDataset,
    key: "appendDataset"
  };
}

export function updateIsSaving(id, isSaving) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: isSaving,
    key: "isSaving"
  };
}

export function updateSortField(id, sortField) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: sortField,
    key: "sortField"
  };
}

export function updateIsPin(id, isPin) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: isPin,
    key: "isPin"
  };
}

export function updateIsBlueprintSaved(id, isBlueprintSaved) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: isBlueprintSaved,
    key: "isBlueprintSaved"
  };
}

export function updateIsBlueprintSaving(id, isBlueprintSaving) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: isBlueprintSaving,
    key: "isBlueprintSaving"
  };
}

export function updateIsCreatingLinkedReport(id, isCreatingLinkedReport) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: isCreatingLinkedReport,
    key: "isCreatingLinkedReport"
  };
}

export function updateIsDatasetUpdating(id, isDatasetUpdating) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: isDatasetUpdating,
    key: "isDatasetUpdating"
  };
}

export function updateOutcomeSuccess(id, outcomeSuccess) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: outcomeSuccess,
    key: "outcomeSuccess"
  };
}

export function updateStopReset(id, stopReset) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: stopReset,
    key: "stopReset"
  };
}

export function updateAddChartId(id, addChartId) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: addChartId,
    key: "addChartId"
  };
}

export function updateIsExploring(id, isExploring) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: isExploring,
    key: "isExploring"
  };
}

export function updateHighlightField(id, highlightField) {
  return (dispatch, getState) => {
    dispatch(updateStopReset(id, true));

    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: highlightField,
      key: "highlightField"
    });
  };
}

export function checkDataset(id, outcome, callback) {
  return (dispatch, getState) => {
    const dataset = homeReducer.getDataset(getState(), id);

    if (dataset && dataset.dataset_status && dataset.dataset_status === "DONE") {
      if (callback !== undefined) {
        return dispatch(callback());
      }

      dispatch(updateIsExploring(id, undefined));

      dispatch(updateSortField(id, { field: outcome, dir: "DESC" }));

      dispatch(updateIsDatasetUpdating(id, false));
    } else {
      setTimeout(() => dispatch(checkDataset(id, outcome, callback)), 3000);
    }
  };
}

export function addNewExploreOutcome(id, outcome) {
  return (dispatch, getState) => {
    const ignoreOutcomes = {
      ...getIgnoreOutcomes(getState(), id),
      [outcome]: false
    };

    dispatch(updateSortField(id, { field: outcome, dir: "DESC" }));

    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: ignoreOutcomes,
      key: "ignoreOutcomes"
    });
  };
}

export function addExploreOutcome(id, outcome, fakeTime) {
  return (dispatch, getState) => {
    const analyzedOutcomes = homeReducer.getAnalyzedOutcomes(getState(), id);
    if (analyzedOutcomes.includes(outcome)) {
      return dispatch(addNewExploreOutcome(id, outcome));
    }

    dispatch(updateIsDatasetUpdating(id, true));
    dispatch(updateIsExploring(id, outcome));

    DatasetAPI.setNewTarget(id, outcome).then((r) => {
      dispatch(homeReducer.updateDataset(id, homeReducer.transformDataset(r)));

      return dispatch(checkDataset(id, outcome));
    });

    return Promise.resolve();
  };
}

export function removeExploreOutcome(id, outcome) {
  return (dispatch, getState) => {
    const ignoreOutcomes = {
      ...getIgnoreOutcomes(getState(), id),
      [outcome]: true
    };

    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: ignoreOutcomes,
      key: "ignoreOutcomes"
    });
  };
}

export function updateColumnBlueprintId(id, columnBlueprintId) {
  return (dispatch, getState) => {
    const dataset = homeReducer.getDataset(getState(), id);
    const columnDescriptions = blueprintReducer.getAllColumnDescription(getState(), columnBlueprintId);
    const blueprintFields = blueprintReducer.getBlueprintFields(getState(), columnBlueprintId);

    const matchedColumns = {};

    Object.keys(blueprintFields).map((c) => {
      const field = blueprintFields[c].label;

      if (dataset && dataset.fields && dataset.fields[field]) {
        matchedColumns[field] = field;
      }
    });

    if (Object.keys(matchedColumns).length > 0 && columnBlueprintId !== undefined) {
      dispatch({
        type: UPDATE_FIELD_VALUE,
        id: id,
        key: "matchedColumns",
        data: matchedColumns
      });
    }

    if (columnBlueprintId === undefined) {
      dispatch({
        type: UPDATE_FIELD_VALUE,
        id: id,
        key: "matchedColumns",
        data: undefined
      });
    }

    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: columnBlueprintId,
      key: "columnBlueprintId"
    });
  };
}

export function saveBlueprintMatches(id, isDelete = false) {
  return (dispatch, getState) => {
    const blueprintId = getColumnBlueprintId(getState(), id);
    const blueprint = blueprintReducer.getBlueprint(getState(), blueprintId);

    const matchedColumns = isDelete ? undefined : getMatchedColumns(getState(), id);

    const linkedDatasets = {
      ...blueprint.wizard.linkedDatasets,
      [id]: matchedColumns
    };

    if (isDelete) {
      delete linkedDatasets[id];
    }

    const retBlueprint = {
      ...blueprint,
      wizard: {
        ...blueprint.wizard,
        linkedDatasets: linkedDatasets
      }
    };

    if (JSON.stringify(retBlueprint) === JSON.stringify(blueprint)) {
      dispatch(updateIsBlueprintSaved(id, true));
      return dispatch(updateIsBlueprintSaving(id, false));
    }

    dispatch(updateIsBlueprintSaving(id, true));

    return BlueprintAPI.updateBlueprint(blueprintId, retBlueprint.wizard)
      .then((response) => {
        toast(isDelete ? "Blueprint Link Deleted" : "Blueprint Link Saved Sucessfully", {
          type: toast.TYPE.SUCCESS,
          position: toast.POSITION.TOP_CENTER,
          autoClose: 7500
        });

        dispatch(updateIsBlueprintSaved(id, true));
        dispatch(updateIsBlueprintSaving(id, false));
        if (isDelete) {
          dispatch(updateColumnBlueprintId(id, undefined));
        }
        return dispatch(blueprintReducer.updateBlueprint(response.id, response));
      })
      .catch((e) => {
        dispatch(updateIsBlueprintSaved(id, true));
        dispatch(updateIsBlueprintSaving(id, false));

        console.log(e);
      });
  };
}

export function updateAttachedBlueprintId(id, attachedBlueprintId) {
  return (dispatch, getState) => {
    const blueprint = blueprintReducer.getBlueprint(getState(), attachedBlueprintId);

    const cGallery = getGallery(getState(), id);

    if (blueprint && blueprint.wizard && blueprint.wizard.gallery && blueprint.wizard.gallery[id]) {
      dispatch(
        updateGallery(id, {
          ...blueprint.wizard.gallery[id],
          rightMode: cGallery.rightMode
        })
      );
    }

    if (blueprint && blueprint.wizard && blueprint.wizard.transform) {
      dispatch(transformReducer.updateTransform(id, blueprint.wizard.transform));
    }

    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: attachedBlueprintId,
      key: "attachedBlueprintId"
    });
  };
}

export function attachGalleryBlueprint(id, jId, failCount = 0) {
  return (dispatch, getState) => {
    if (failCount > 10) {
      return Promise.resolve();
    }

    const blueprint = blueprintReducer.getBlueprint(getState(), jId);

    if (blueprint === undefined) {
      return setTimeout(() => dispatch(attachGalleryBlueprint(id, jId, failCount++)), 1000);
    }

    const cGallery = getGallery(getState(), id);

    if (blueprint && blueprint.wizard && blueprint.wizard.gallery && blueprint.wizard.gallery[id]) {
      dispatch(
        updateGallery(id, {
          ...blueprint.wizard.gallery[id],
          rightMode: cGallery ? cGallery.rightMode : undefined,
          jId: jId
        })
      );
    }

    if (blueprint && blueprint.wizard && blueprint.wizard.transform) {
      dispatch(transformReducer.updateTransform(id, blueprint.wizard.transform));
    }

    return Promise.resolve();
  };
}

export function updateRightMode(id, rightMode) {
  return (dispatch, getState) => {
    const cRightMode = getRightMode(getState(), id);

    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      key: "rightMode",
      data: cRightMode !== undefined && rightMode === cRightMode ? undefined : rightMode
    });
  };
}

export function updateViewField(id, viewField, noUpdateRightMode = false) {
  return (dispatch, getState) => {
    const cViewField = getViewField(getState(), id);

    dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      key: "viewField",
      data: noUpdateRightMode === false && cViewField === viewField ? undefined : viewField
    });

    return (
      noUpdateRightMode === false &&
      dispatch({
        type: UPDATE_FIELD_VALUE,
        id: id,
        key: "rightMode",
        data: viewField === undefined || cViewField === viewField ? undefined : GALLERY_RIGHT_MODE.FIELD
      })
    );
  };
}

export function updateViewOutcome(id, viewOutcome) {
  return (dispatch, getState) => {
    dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      key: "viewOutcome",
      data: viewOutcome
    });

    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      key: "rightMode",
      data: viewOutcome === undefined ? undefined : GALLERY_RIGHT_MODE.OUTCOME
    });
  };
}

export function updateLookupKey(id, keyIndex, lookupKey) {
  return (dispatch, getState) => {
    const lookupKeys = {
      ...getLookupKeys(getState(), id),
      [keyIndex]: lookupKey
    };

    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      key: "lookupKeys",
      data: lookupKeys
    });
  };
}

export function updateMatchedColumn(id, datasetColumn, blueprintColumn) {
  return (dispatch, getState) => {
    const matchedColumns = {
      ...getMatchedColumns(getState(), id),
      [datasetColumn]: blueprintColumn
    };

    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      key: "matchedColumns",
      data: matchedColumns
    });
  };
}

export function updateFilter(id, keyIndex, filter) {
  return (dispatch, getState) => {
    const filters = {
      ...getFilters(getState(), id),
      [keyIndex]: filter
    };

    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      key: "filters",
      data: filters
    });
  };
}

export function updateJoinType(id, joinType) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    key: "joinType",
    data: joinType
  };
}

export function updateAttachedBlueprint(id, blueprintId, isAttached) {
  return (dispatch, getState) => {
    const attachedBlueprints = {
      ...getAttachedBlueprints(getState(), id),
      [blueprintId]: isAttached
    };

    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      key: "attachedBlueprints",
      data: attachedBlueprints
    });
  };
}

// This will never cause isuses
export function saveChanges(id, isNew = false) {
  return (dispatch, getState) => {
    dispatch(updateIsSaving(id, true));
    const dataset = homeReducer.getDataset(getState(), id);

    const name = (dataset ? dataset.label : "") + " " + moment.utc().local().format("MMMM Do YYYY, h:mm:ss a");

    let gallery = {
      gallery: { ...getBase(getState(), id) },
      name: name,
      galleryId: id,
      datasetId: id,
      transform: { ...transformReducer.getTransform(getState(), id) },
      isSaving: false
    };

    const folderId = foldersReducer.getSelectedFolder(getState());

    return BlueprintAPI.createBlueprint(gallery, folderId, name).then((response) => {
      toast("Dataset Sucessfully Updated", {
        type: toast.TYPE.SUCCESS,
        position: toast.POSITION.TOP_CENTER,
        autoClose: 7500
      });

      dispatch(updateIsSaving(id, false));

      return dispatch(blueprintReducer.updateBlueprint(response.id, response));
    });
  };
}

export function createAI(id, outcome, success) {
  return (dispatch, getState) => {
    const dataset = homeReducer.getDataset(getState(), id);

    if (dataset && dataset.outcome !== outcome) {
      DatasetAPI.setNewTarget(id, outcome).then((r) => {
        dispatch(homeReducer.updateDataset(id, homeReducer.transformDataset(r)));

        return dispatch(checkDataset(id, outcome, () => createAIHelper(id, outcome, success)));
      });
    } else {
      dispatch(createAIHelper(id, outcome, success));
    }
  };
}

export function createAIHelper(id, outcome, success) {
  return (dispatch, getState) => {
    const runId = "new";

    dispatch(setupReducer.updateDatasetId(runId, id, true));

    dispatch(setupReducer.updateOutcome(runId, outcome));
    dispatch(homeReducer.changeOutcome(id, outcome));
    success && dispatch(setupReducer.updateSuccess(runId, success));

    dispatch(setupReducer.introspectCounts(runId));

    //dispatch(globalReducer.updateIsBusinessMode(true));

    const columnBlueprintId = getColumnBlueprintId(getState(), id);
    if (columnBlueprintId) {
      //dispatch(setupReducer.initCustomBlueprint(runId, columnBlueprintId));
    }

    const setup = setupReducer.getSetup(getState(), runId);

    return dispatch(
      setupReducer.initJobFromWizard(runId, columnBlueprintId, {
        createProject: true
      })
    );
  };
}

export function checkBlueprints(id) {
  return (dispatch, getState) => {
    const blueprints = viewReducer.getBlueprints(getState());

    if (blueprints === undefined || blueprints.length === 0) {
      return setTimeout(() => dispatch(checkBlueprints(id)), 200);
    } else {
      const bpMatch = blueprints.filter((b) => b.wizard && b.wizard.linkedDatasets && b.wizard.linkedDatasets[id]);

      if (bpMatch && bpMatch.length > 0) {
        dispatch(updateColumnBlueprintId(id, bpMatch[0].id));
        dispatch(updateIsBlueprintSaved(id, true));
      }
    }
  };
}

export function checkDatasetExist(id) {
  return (dispatch, getState) => {
    const dataset = homeReducer.getDataset(getState(), id);

    if (dataset === undefined || dataset.fields === undefined || dataset.fields.length === 0) {
      return setTimeout(() => dispatch(checkDatasetExist(id)), 200);
    } else {
      const outcomes = homeReducer.getAnalyzedOutcomes(getState(), id);

      if (outcomes && outcomes.length > 0) {
        dispatch(updateSortField(id, { field: outcomes[0], dir: "DESC" }));
      }
    }
  };
}

export function init(id, jId, isNext) {
  return (dispatch, getState) => {
    const gallery = getGallery(getState(), id);
    const stopReset = getStopReset(getState(), id);

    if (gallery === undefined && stopReset !== true) {
      dispatch(updateGallery(id, {}));
    }

    if (stopReset === true) {
      dispatch(updateStopReset(id, false));
    }

    dispatch(checkBlueprints(id));
    dispatch(checkDatasetExist(id));

    if (jId !== undefined) {
      dispatch(attachGalleryBlueprint(id, jId));
    }

    const sFolderId = foldersReducer.getSelectedFolder(getState());

    return dispatch(homeReducer.fetchDataset(id)).then((nData) => {
      const dataset = homeReducer.getDataset(getState(), id);
      if (dataset && dataset.folder_id !== sFolderId && !isNext) {
        dispatch(foldersReducer.updateSelectedFolder(dataset.folder_id));
      }
      return nData;
    });
  };
}

export default galleryReducer;
